import { setAccessToken } from "@/utils/api";
import type { ApiUser } from "@shared/types/models";
import type { UserRole } from "@shared/constants";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from "react";

export type AccessClaims = {
  sub: string;
  role: UserRole;
};

export type AuthState = {
  user: ApiUser | null;
  accessToken: string | null;
  claims: AccessClaims | null;
};

export type AuthAction =
  | {
      type: "LOGIN";
      payload: {
        user: ApiUser;
        accessToken: string;
      };
    }
  | { type: "LOGOUT" }
  | { type: "UPDATE_USER"; payload: Partial<ApiUser> }
  | { type: "SET_ACCESS_TOKEN"; payload: string | null }
  | { type: "SET_CLAIMS"; payload: AccessClaims | null }
  | { type: "SET_AUTH"; payload: AuthState };
const STORAGE_KEY = "friexp.ui";

type UIShape = Pick<AuthState, "user" | "accessToken">;

const readUI = (): UIShape => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : { user: null, accessToken: null };
  } catch {
    return { user: null, accessToken: null };
  }
};

const decodeClaims = (token: string | null): AccessClaims | null => {
  if (!token) return null;
  try {
    const [, payload] = token.split(".");
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    const utf8 = decodeURIComponent(
      Array.from(
        json,
        (c) => "%" + c.charCodeAt(0).toString(16).padStart(2, "0"),
      ).join(""),
    );
    return JSON.parse(utf8);
  } catch {
    return null;
  }
};

const initialState: AuthState = {
  ...readUI(),
  claims: decodeClaims(readUI().accessToken ?? null),
};

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case "LOGIN": {
      const { user, accessToken } = action.payload;
      return {
        user,
        accessToken,
        claims: decodeClaims(accessToken),
      };
    }

    case "LOGOUT":
      return { user: null, accessToken: null, claims: null };

    case "UPDATE_USER":
      return {
        ...state,
        user: state.user ? { ...state.user, ...action.payload } : null,
      };

    case "SET_ACCESS_TOKEN":
      return {
        ...state,
        accessToken: action.payload,
        claims: decodeClaims(action.payload),
      };

    case "SET_CLAIMS":
      return { ...state, claims: action.payload };

    case "SET_AUTH":
      return { ...action.payload };

    default:
      return state;
  }
};

export const AuthContext = createContext<
  { state: AuthState; dispatch: React.Dispatch<AuthAction> } | undefined
>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used with <AuthProvider>");
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    setAccessToken(state.accessToken);
  }, [state.accessToken]);

  useEffect(() => {
    try {
      const ui: UIShape = {
        user: state.user,
        accessToken: state.accessToken,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(ui));
    } catch {}
  }, [state.user, state.accessToken]);

  const value = useMemo(() => ({ state, dispatch }), [state]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
